import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ExternalLink } from '@/components/ExternalLink';

const footerLinks = {
  about: [
    { name: 'Schreiner Dynasty', href: '/schreiner-dynasty' },
    { name: 'Captain Schreiner', href: '/captain-schreiner' },
    { name: 'Charles Schreiner III', href: '/schreiner-iii' },
    { name: 'Ranch History', href: '/ranch-history' },
    { name: 'First Peoples of the Y.O.', href: '/native-american-heritage' },
    { name: 'Culture & Fame', href: '/culture-and-fame' },
    { name: 'The Museum', href: '/museum' },
    { name: 'Explore', href: '/explore' },
  ],
  topics: [
    { name: 'Texas Rangers', href: '/texas-rangers' },
    { name: 'Texas Longhorns', href: '/texas-longhorn-heritage' },
    { name: 'Exotic Wildlife Heritage', href: '/wildlife-heritage' },
    { name: 'Famous Visitors', href: '/famous-visitors' },
    { name: 'Documents', href: '/document-search' },
  ],
  visit: [
    { name: 'Plan Your Visit', href: '/visit' },
    { name: 'Explore Exhibits', href: '/explore?view=stories' },
    { name: 'Contact Us', href: '/contact' },
    { name: 'Book a Room', href: 'https://yoranchheadquarters.com', external: true },
  ],
};

const FooterLinkGroup = ({ 
  title, 
  links 
}: { 
  title: string; 
  links: { name: string; href: string; external?: boolean }[] 
}) => (
  <div>
    <h4 className="font-sans text-[10px] font-medium tracking-[0.2em] uppercase text-brass mb-6">
      {title}
    </h4>
    <ul className="space-y-3">
      {links.map((link) => (
        <li key={link.href}>
          {link.external ? (
            <ExternalLink
              href={link.href}
              className={cn(
                "group inline-flex items-center gap-1 font-sans text-sm text-hero-text/70",
                "hover:text-hero-text transition-all duration-300",
                "hover:translate-x-1"
              )}
            >
              <span className="relative">
                {link.name}
                <span className="absolute bottom-0 left-0 w-0 h-px bg-brass/50 group-hover:w-full transition-all duration-300" />
              </span>
              <ArrowUpRight className="w-3 h-3 opacity-50" aria-hidden="true" />
            </ExternalLink>
          ) : (
            <Link 
              to={link.href} 
              className={cn(
                "group inline-flex items-center gap-1 font-sans text-sm text-hero-text/70",
                "hover:text-hero-text transition-all duration-300",
                "hover:translate-x-1"
              )}
            >
              <span className="relative">
                {link.name}
                <span className="absolute bottom-0 left-0 w-0 h-px bg-brass/50 group-hover:w-full transition-all duration-300" />
              </span>
            </Link>
          )}
        </li>
      ))}
    </ul>
  </div>
);

const Footer = () => {
  return (
    <footer className="bg-hero text-hero-text relative overflow-hidden">
      {/* Subtle background texture */}
      <div className="absolute inset-0 opacity-[0.02] bg-[radial-gradient(circle_at_50%_50%,_hsl(var(--hero-text))_1px,_transparent_1px)] bg-[length:24px_24px]" />
      
      {/* Decorative top border */}
      <div className="h-px bg-gradient-to-r from-transparent via-brass/30 to-transparent" />
      
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 relative">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <Link to="/" className="inline-block group">
              <h3 className="font-display text-lg font-normal tracking-widest uppercase text-hero-text group-hover:text-brass transition-colors duration-300">
                Y.O. Ranch Headquarters
              </h3>
              <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-hero-text/50 mt-2">
                Texas History Museum
              </p>
            </Link>
            <div className="w-12 h-px bg-gradient-to-r from-brass/40 to-transparent mt-6 mb-4" />
            <p className="font-serif text-sm text-hero-text/60 leading-relaxed italic max-w-xs">
              A private, appointment-only museum experience preserving the heritage of the Texas Hill Country.
            </p>
          </div>

          <FooterLinkGroup title="Y.O. Ranch Heritage" links={footerLinks.about} />
          <FooterLinkGroup title="Topics" links={footerLinks.topics} />

          {/* Visit - Special styling */}
          <div>
            <h4 className="font-sans text-[10px] font-medium tracking-[0.2em] uppercase text-brass mb-6">
              Visit
            </h4>
            <ul className="space-y-3">
              {footerLinks.visit.map((link) => (
                <li key={link.href}>
                  {link.external ? (
                    <ExternalLink
                      href={link.href}
                      className={cn(
                        "group inline-flex items-center gap-1.5 font-sans text-sm text-hero-text/70",
                        "hover:text-hero-text transition-all duration-300",
                        "hover:translate-x-1"
                      )}
                    >
                      <span className="relative">
                        {link.name}
                        <span className="absolute bottom-0 left-0 w-0 h-px bg-brass/50 group-hover:w-full transition-all duration-300" />
                      </span>
                      <ArrowUpRight className="w-3 h-3 opacity-70" aria-hidden="true" />
                    </ExternalLink>
                  ) : (
                    <Link
                      to={link.href}
                      className={cn(
                        "group inline-flex items-center gap-1.5 font-sans text-sm text-hero-text/70",
                        "hover:text-hero-text transition-all duration-300",
                        "hover:translate-x-1"
                      )}
                    >
                      <span className="relative">
                        {link.name}
                        <span className="absolute bottom-0 left-0 w-0 h-px bg-brass/50 group-hover:w-full transition-all duration-300" />
                      </span>
                      <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-70 group-hover:translate-x-0 transition-all duration-300" />
                    </Link>
                  )}
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-4 border-t border-hero-text/10">
              <p className="font-sans text-xs text-brass tracking-wide">
                By Appointment Only
              </p>
              <p className="font-serif text-xs text-hero-text/40 italic mt-1">
                For registered guests
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer Bar */}
      <div className="border-t border-hero-text/10 relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <p className="font-sans text-xs text-hero-text/40 leading-relaxed max-w-2xl">
              All exhibits and collections are presented for historical and educational purposes only. 
              The museum does not engage in firearm sales, transfers, or commercial transactions. 
              Access is limited to registered Y.O. Ranch Headquarters guests by appointment only.
            </p>
            <p className="font-sans text-xs text-hero-text/30 whitespace-nowrap">
              © {new Date().getFullYear()} Y.O. Ranch Headquarters
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;